/**
 * EIDConfirmationPopup Component
 *
 * Confirmation dialog shown when user clicks Continue on the EID entry step.
 * Warns user that an incorrect EID will require restarting the process.
 */

import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import { CardHeader } from '../../../shared/card/CardHeader';
import { Icon } from '../../../shared/Icon';
import { KEYS } from '../../../../keys';

/**
 * Props for the EIDConfirmationPopup component
 */
export interface EIDConfirmationPopupProps {
  /** The EID value entered by the user */
  eid: string;
  /** Callback when user confirms the EID - can be async */
  onConfirm: () => void | Promise<void>;
  /** Callback when user cancels/goes back */
  onCancel: () => void;
}

/**
 * EIDConfirmationPopup component
 */
export const EIDConfirmationPopup: React.FC<EIDConfirmationPopupProps> = ({
  eid,
  onConfirm,
  onCancel,
}: EIDConfirmationPopupProps): React.ReactElement => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleConfirm = React.useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm]);

  return (
    <PopupContainer data-testid={KEYS.WIZARD_EID_CONFIRM_POPUP}>
      <PopupCard>
        <CardHeader surface={8}>
          <HeaderContent>
            <Icon name='warning' size='small' color='#ffa726' />
            <h5>Confirm Equipment ID</h5>
          </HeaderContent>
        </CardHeader>
        <CardBody>
          <ContentContainer>
            <EIDDisplayBox>
              <EIDLabel>Equipment ID (EID)</EIDLabel>
              <EIDValue>{eid}</EIDValue>
            </EIDDisplayBox>
            <WarningMessage>
              <Icon name='info' size='tiny' color='rgba(255, 255, 255, 0.72)' />
              <span>Please verify this is correct.</span>
            </WarningMessage>
          </ContentContainer>
        </CardBody>
        <CardFooter>
          <CardFooterLeft>
            <Button
              title='Go Back'
              flavor='secondary'
              onClick={onCancel}
              disabled={isLoading}
              data-testid={KEYS.WIZARD_EID_CANCEL_BUTTON}
            />
          </CardFooterLeft>
          <CardFooterRight>
            <Button
              title='Confirm'
              flavor='primary'
              onClick={handleConfirm}
              disabled={isLoading}
              data-testid={KEYS.WIZARD_EID_CONFIRM_BUTTON}
            >
              {isLoading && <Spinner />}
            </Button>
          </CardFooterRight>
        </CardFooter>
      </PopupCard>
    </PopupContainer>
  );
};

// Styled Components

const PopupContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 103;
`;

const PopupCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  background: var(--color-popup-background);
  border-radius: 10px;
  box-shadow: var(--shadow-elevation-12);
  overflow: hidden;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 0;

  h5 {
    margin: 0;
    font-weight: bold;
    color: var(--color-text);
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
`;

const EIDDisplayBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--color-surface-2);
  border-radius: 8px;
`;

const EIDLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.56);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EIDValue = styled.span`
  font-size: 32px;
  font-family: monospace;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: 4px;
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 167, 38, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(255, 167, 38, 0.2);

  span {
    font-size: 13px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.72);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-left: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export default EIDConfirmationPopup;
